// Acenda onboarding — talks only to control-tower's /api/manage-acenda, which
// does all the cross-service glue (acenda-app-config + oms-config + provider
// doc) and validates the credentials against Acenda before writing anything.
//
// The shape of this differs from pipe17Service in two ways that are worth
// stating, because both come from Acenda itself rather than from preference:
//
//  1. THE ORGANIZATION MUST BE TYPED IN. Pipe17's org_key is auto-detected from
//     the account's own records; Acenda's `organization` is a request HEADER
//     (`X-Astur-Organization`), not a resource, so there is nothing to read it
//     off. No amount of probing recovers it.
//
//  2. THERE ARE TWO CREDENTIAL MODES. Acenda issues either a static admin-panel
//     API key (Users > API Keys) used as the bearer outright, or an OAuth2
//     client_id/client_secret pair exchanged for a short-lived token. Acenda's
//     own docs disagree about which applies — the v3.2 guide describes the key
//     while naming the Postman collection (which ships the OAuth2 flow) as the
//     source of truth — so the backend accepts either and the form asks which.
//
// There is also NO webhook step and no backfill-vs-webhook distinction: Acenda
// pushes nothing, so the provider polls on a schedule once the client is
// onboarded. Onboarding runs from the Add OMS dialog, the same place Pipe17 is
// onboarded — there is deliberately no separate Acenda page.

import axiosInstance from "../axiosInstance";

export const ACENDA_PROVIDER_NAME = "Acenda";

export const AUTH_MODES = {
  API_KEY: "api_key",
  OAUTH: "oauth",
};

/**
 * Is this row's credential input complete enough to submit? Keeps the
 * either/or rule (OAuth pair vs static key) in one place rather than inline in
 * the dialog, so the submit guard and the payload builder cannot disagree.
 */
export const acendaCredentialsComplete = (row = {}) => {
  if (!row.organization || !String(row.organization).trim()) return false;
  if (row.auth_mode === AUTH_MODES.OAUTH) {
    return Boolean(
      String(row.acenda_client_id || "").trim() &&
        String(row.acenda_client_secret || "").trim(),
    );
  }
  return Boolean(String(row.api_key || "").trim());
};

/** Build the POST body for whichever mode the operator chose. */
export const buildOnboardPayload = (row = {}) => {
  const payload = {
    client_id: row.client_id,
    organization: String(row.organization || "").trim(),
  };
  if (row.auth_mode === AUTH_MODES.OAUTH) {
    payload.acenda_client_id = String(row.acenda_client_id || "").trim();
    payload.acenda_client_secret = String(row.acenda_client_secret || "").trim();
  } else {
    payload.api_key = String(row.api_key || "").trim();
  }
  // Optional routing overrides. Left out entirely when blank so the backend
  // derives them from the org's own warehouses instead of storing a blank.
  if (row.warehouses && String(row.warehouses).trim()) {
    payload.warehouses = String(row.warehouses).trim();
  }
  return payload;
};

export const acendaService = {
  // Onboard a client: validates the credentials against Acenda, records the
  // routing defaults from the org's warehouses, ensures the provider +
  // oms-config docs, and persists the credentials.
  onboard: async (data) => {
    const response = await axiosInstance.post("/manage-acenda", data);
    return response.data;
  },
};
